import * as React from 'react';

import { enumAddressType } from '@rematter/frontend-lib-api-client';
import { FacilityRecord } from '@rematter/modules/facilities/types/facility';
import { Button } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import * as facilityFormLocalState from '../../_state/facilityForm.localState';
import { useCreateFacilityMutation } from '../../hooks/mutations/useCreateFacilityMutation';
import { useUpdateFacilityMutation } from '../../hooks/mutations/useUpdateFacilityMutation';

import { FacilityForm } from './FacilityForm';

import { useTranslations } from '@/facilities/i18n/useTranslations';

interface FacilityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  facility?: FacilityRecord;
  onSuccess?: () => void;
}

export const FacilityDialog = ({
  isOpen,
  onClose,
  facility,
  onSuccess,
}: FacilityDialogProps) => {
  const toast = useToast();
  const t = useTranslations();
  const { mutateAsync: createFacility, isPending: isCreating } =
    useCreateFacilityMutation();
  const { mutateAsync: updateFacility, isPending: isUpdating } =
    useUpdateFacilityMutation();

  const formData = useAtomValue(facilityFormLocalState.$facilityForm);
  const resetForm = useSetAtom(facilityFormLocalState.$resetFacilityForm);
  const updateForm = useSetAtom(facilityFormLocalState.$updateFacilityForm);

  React.useEffect(() => {
    if (isOpen && facility) {
      updateForm({
        facilityName: facility.facilityName,
        facilityType: facility.facilityType,
        timezone: '',
        address: {
          streetAddress: facility.address?.streetAddress ?? '',
          city: facility.address?.city ?? '',
          state: facility.address?.state ?? '',
          country: facility.address?.country ?? '',
          postalCode: facility.address?.postalCode ?? '',
        },
      });
    }
  }, [isOpen, facility, updateForm]);

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    try {
      const input = {
        facilityName: formData.facilityName,
        facilityType: formData.facilityType,
        timezone: formData.timezone,
        customAttributes: formData.customAttributes,
        address: {
          ...formData.address,
          addressType: enumAddressType.Standard,
          isMailingAddress: true,
        },
      };

      if (facility) {
        await updateFacility({
          id: facility.id,
          ...input,
        });
        toast.success(t.facilities.form.messages.updateSuccess);
      } else {
        await createFacility(input);
        toast.success(t.facilities.form.messages.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? (error.message as never)
          : t.facilities.form.messages.error,
      );
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@FacilityDialog"
    >
      <DialogContent slot="title" position="center">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {facility
              ? t.facilities.dialog.title.edit
              : t.facilities.dialog.title.create}
          </div>
        </DialogHeader>

        <div className="p-4">
          <FacilityForm isEditing={!!facility} />
        </div>

        <DialogFooter>
          <Button
            color="failure"
            variant="outline"
            isPending={isSubmitting}
            onPress={handleClose}
          >
            {t.facilities.dialog.actions.cancel}
          </Button>
          <Button
            color="primary"
            variant="solid"
            isPending={isSubmitting}
            isLoading={isSubmitting}
            onPress={handleSubmit}
          >
            {facility
              ? t.facilities.dialog.actions.update
              : t.facilities.dialog.actions.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
